import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
  Switch,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { FiSettings } from "react-icons/fi";
import { Field, FieldArray, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useMemo } from "react";
import {
  getConfigEngineById,
  getTypeRouters,
  updateConfigEngine,
  createConfigEngine,
  deleteConfigEngine,
  getListEntityConfigEngine,
} from "@/services/consignado/config-engine.service";
import { FormikErrors } from "formik";
import {
  UpdateConfigEngineDTO,
  CreateConfigEngineDTO,
  TipoRotaResposta,
  ConfiguracaoCredito,
  GetConfigEngineResponse,
} from "@/services/consignado/dto/config-engine.dto";

interface EngineConfigItem {
  id?: number;
  responseRouteType: string;
  property: string;
  fieldToSave: string;
  displayLabel: string;
  entityToSave: string;
  visibleInReport: boolean;
}

interface EngineConfigFormValues {
  responseRouteType: string;
  responseRouteDescription: string;
  configs: EngineConfigItem[];
}

const createEngineConfigSchema = Yup.object().shape({
  responseRouteType: Yup.string().required(
    "Tipo de Rota Resposta é obrigatório"
  ),
  responseRouteDescription: Yup.string().required(
    "Descrição do Tipo de Rota Resposta é obrigatória"
  ),
  configs: Yup.array()
    .of(
      Yup.object().shape({
        property: Yup.string().required("Propriedade é obrigatória"),
        displayLabel: Yup.string().required(
          "Rótulo para Exibição é obrigatório"
        ),
        fieldToSave: Yup.string(),
        entityToSave: Yup.string(),
        visibleInReport: Yup.boolean(),
      })
    )
    .min(1, "Adicione pelo menos uma configuração"),
});

const updateEngineConfigSchema = Yup.object().shape({
  responseRouteType: Yup.string().required(
    "Tipo de Rota Resposta é obrigatório"
  ),
  responseRouteDescription: Yup.string().required(),
  configs: Yup.array()
    .of(
      Yup.object().shape({
        property: Yup.string().required("Propriedade é obrigatória"),
        displayLabel: Yup.string().required(
          "Rótulo para Exibição é obrigatório"
        ),
        fieldToSave: Yup.string(),
        entityToSave: Yup.string(),
        visibleInReport: Yup.boolean(),
      })
    )
    .min(1, "Adicione pelo menos uma configuração"),
});

const mapFormValuesToUpdatePayload = (
  values: EngineConfigFormValues,
  tipoRotaResposta: TipoRotaResposta | null
): UpdateConfigEngineDTO | null => {
  if (!tipoRotaResposta) {
    return null;
  }

  const configuracaoCredito: ConfiguracaoCredito[] = values.configs.map(
    (config) => ({
      id: config.id,
      tipo_resposta_rota: values.responseRouteType,
      propriedade: config.property,
      relatorio_visivel: config.visibleInReport,
      rotulo: config.displayLabel,
      ...(config.entityToSave && { entidade_para_salvar: config.entityToSave }),
      ...(config.fieldToSave && { campo_para_salvar: config.fieldToSave }),
    })
  );

  return {
    tipo_rota_resposta: tipoRotaResposta,
    configuracao_credito: configuracaoCredito,
  };
};

const mapFormValuesToCreatePayload = (
  values: EngineConfigFormValues
): CreateConfigEngineDTO => {
  const configuracaoCredito = values.configs.map((config) => ({
    tipo_resposta_rota: values.responseRouteType,
    propriedade: config.property,
    relatorio_visivel: config.visibleInReport,
    rotulo: config.displayLabel,
    ...(config.fieldToSave && { campo_para_salvar: config.fieldToSave }),
    ...(config.entityToSave && { entidade_para_salvar: config.entityToSave }),
  }));

  return {
    tipo_rota_resposta: {
      nome: values.responseRouteType,
      descricao: values.responseRouteDescription,
      ativo: true,
    },
    configuracao_credito: configuracaoCredito,
  };
};

const mapApiToFormValues = (
  data: GetConfigEngineResponse | null | any
): EngineConfigFormValues => {
  // console.log("mapApiToFormValues - data:", data);

  if (!data) {
    console.log("No data provided");
    return {
      responseRouteType: "",
      responseRouteDescription: "",
      configs: [],
    };
  }

  // Verificar se é um array (estrutura antiga) ou objeto (estrutura nova)
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return {
        responseRouteType: "",
        responseRouteDescription: "",
        configs: [],
      };
    }
    const responseRouteType = data[0]?.tipo_resposta_rota || "";
    return {
      responseRouteType,
      responseRouteDescription: "",
      configs: data.map((item) => ({
        id: item.id,
        responseRouteType: item.tipo_resposta_rota || "",
        property: item.propriedade || "",
        fieldToSave: item.campo_para_salvar || "",
        displayLabel: item.rotulo || "",
        entityToSave: item.entidade_para_salvar || "",
        visibleInReport: item.relatorio_visivel || false,
      })),
    };
  }

  if (!data.tipo_rota_resposta || !data.configuracao_credito) {
    return {
      responseRouteType: "",
      responseRouteDescription: "",
      configs: [],
    };
  }

  return {
    responseRouteType: data.tipo_rota_resposta.nome || "",
    responseRouteDescription: data.tipo_rota_resposta.descricao || "",
    configs: (data.configuracao_credito || []).map((item: any) => ({
      id: item.id,
      responseRouteType: item.tipo_resposta_rota || "",
      property: item.propriedade || "",
      fieldToSave: item.campo_para_salvar || "",
      displayLabel: item.rotulo || "",
      entityToSave: item.entidade_para_salvar || "",
      visibleInReport: item.relatorio_visivel || false,
    })),
  };
};

const getFormError = (
  errors: FormikErrors<EngineConfigFormValues>,
  path: (string | number)[]
): string | undefined => {
  let current: any = errors;

  for (const key of path) {
    if (current == null) return undefined;
    if (typeof current === "string") return current;
    current = current[key];
  }

  return typeof current === "string" ? current : undefined;
};

export default function ConsignadoEngineConfigEditor() {
  const { id } = useParams<{ id: string }>();
  const isCreate: boolean = id === undefined || id === "novo";
  const navigate = useNavigate();
  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["config-engine", id],
    queryFn: async () => {
      if (!isCreate && id) {
        const result = await getConfigEngineById(parseInt(id));
        return result;
      }
      return null;
    },
    refetchOnWindowFocus: false,
    enabled: !isCreate && !!id,
  });

  const { data: listEntityConfigEngine } = useQuery({
    queryKey: ["list-entity-config-engine"],
    queryFn: async () => {
      return await getListEntityConfigEngine();
    },
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const { data: typeRouters } = useQuery({
    queryKey: ["type-routers"],
    queryFn: async () => {
      return await getTypeRouters();
    },
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const initialValues: EngineConfigFormValues = useMemo(() => {
    const mapped = mapApiToFormValues(data as GetConfigEngineResponse | null);
    return mapped;
  }, [data]);

  const handleCreateConfigEngine = useMutation({
    mutationFn: async (formValues: EngineConfigFormValues) => {
      const createPayload = mapFormValuesToCreatePayload(formValues);
      await createConfigEngine(createPayload);
      return;
    },
    onSuccess() {
      toast({
        title: "Sucesso",
        description: "Configuração do motor criada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/consignado/engine-config");
    },
    onError(error: any) {
      if (
        error?.response?.data?.message.includes(
          "Tipo de rota resposta já existe"
        )
      ) {
        toast({
          title: "Aviso!",
          description:
            error?.response?.data?.message || "Tipo de rota resposta já existe",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: "Erro",
        description: error?.message || "Erro ao criar configuração do motor",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleDeleteConfigEngine = useMutation({
    mutationFn: async (id: number) => {
      return await deleteConfigEngine(id);
    },
    onSuccess() {
      toast({
        title: "Sucesso",
        description: "Configuração do motor excluída com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError(error: any) {
      toast({
        title: "Erro",
        description: error?.message || "Erro ao excluir configuração do motor",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleUpdateConfigEngine = useMutation({
    mutationFn: async (formValues: EngineConfigFormValues) => {
      if (!id || isCreate) {
        throw new Error("ID da configuração não encontrado");
      }

      let tipoRotaResposta: TipoRotaResposta;

      if (data && (data as GetConfigEngineResponse).tipo_rota_resposta) {
        const loadedData = data as GetConfigEngineResponse;
        tipoRotaResposta = {
          ...loadedData.tipo_rota_resposta,
          nome: formValues.responseRouteType, // Atualizar o nome se foi alterado
        };
      } else {
        const tipoRotaRespostaFound = (typeRouters as any[])?.find(
          (tr: any) => tr.nome === formValues.responseRouteType
        );

        if (!tipoRotaRespostaFound) {
          throw new Error(
            `Tipo de rota resposta "${formValues.responseRouteType}" não encontrado`
          );
        }

        // Criar o objeto tipo_rota_resposta completo
        tipoRotaResposta = {
          id: tipoRotaRespostaFound.id,
          nome: formValues.responseRouteType,
          descricao: tipoRotaRespostaFound.descricao,
          ativo: tipoRotaRespostaFound.ativo,
          criado_em: tipoRotaRespostaFound.criado_em,
        };
      }

      const updatePayload = mapFormValuesToUpdatePayload(
        formValues,
        tipoRotaResposta
      );

      if (!updatePayload) {
        throw new Error("Erro ao preparar dados para atualização");
      }

      await updateConfigEngine(parseInt(id), updatePayload);
      return;
    },
    onSuccess() {
      toast({
        title: "Sucesso",
        description: "Configuração do motor atualizada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/consignado/engine-config");
    },
    onError(error: any) {
      toast({
        title: "Erro",
        description:
          error?.message || "Erro ao atualizar configuração do motor",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
      >
        <Flex
          alignItems={{ base: "center", md: "center" }}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Flex flexDirection="column" mb={{ base: 4, md: 0 }}>
            <Flex alignItems="center">
              <FiSettings size="2rem" />
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                ml={4}
                fontFamily="Lexend Deca"
              >
                Configuração do Motor
              </Text>
            </Flex>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              ml={12}
              mt={2}
              fontFamily="Lexend Deca"
              opacity={0.9}
            >
              {isCreate ? "Criar nova configuração" : "Editar configuração"}
            </Text>
          </Flex>
        </Flex>
      </Box>

      <Box p={{ base: 4, md: 6 }} mb={4} boxShadow="md" width="100%">
        {!isCreate && (isLoading || isFetching) ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <Formik<EngineConfigFormValues>
            key={`${id || "new"}-${data ? "loaded" : "empty"}`} // Força reinicialização quando o ID ou dados mudam
            initialValues={initialValues}
            validationSchema={
              isCreate ? createEngineConfigSchema : updateEngineConfigSchema
            }
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={true}
            onSubmit={(values, { resetForm }) => {
              isCreate
                ? handleCreateConfigEngine.mutate(values, {
                    onSuccess() {
                      resetForm();
                    },
                  })
                : handleUpdateConfigEngine.mutate(values);
            }}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
            }: FormikProps<EngineConfigFormValues>) => (
              <Form style={{ width: "100%" }}>
                <FieldArray name="configs">
                  {({ push, remove }) => (
                    <>
                      <Flex
                        w="100%"
                        gap={5}
                        flexDir={["column", "column", "row", "row"]}
                        alignItems="start"
                      >
                        <Flex
                          w="100%"
                          flexWrap={["wrap", "wrap", "wrap", "nowrap"]}
                          alignItems="start"
                          gap={5}
                        >
                          <FormControl
                            isInvalid={
                              !!getFormError(errors, ["responseRouteType"]) &&
                              touched.responseRouteType
                            }
                            isRequired
                          >
                            <FormLabel
                              fontFamily="Lexend Deca"
                              htmlFor="responseRouteType"
                            >
                              Tipo de Rota Resposta
                            </FormLabel>
                            <Field
                              as={Input}
                              fontFamily="Lexend Deca"
                              id="responseRouteType"
                              name="responseRouteType"
                              type="text"
                              placeholder="Ex: listar-autorizados"
                            />
                            <FormErrorMessage>
                              {getFormError(errors, ["responseRouteType"])}
                            </FormErrorMessage>
                          </FormControl>
                          <FormControl
                            isInvalid={
                              !!getFormError(errors, ["responseRouteType"]) &&
                              touched.responseRouteType
                            }
                            isRequired
                          >
                            <FormLabel
                              fontFamily="Lexend Deca"
                              htmlFor="responseRouteType"
                            >
                              Descrição
                            </FormLabel>
                            <Field
                              as={Input}
                              fontFamily="Lexend Deca"
                              id="responseRouteDescription"
                              name="responseRouteDescription"
                              type="text"
                            />
                            <FormErrorMessage>
                              {getFormError(errors, [
                                "responseRouteDescription",
                              ])}
                            </FormErrorMessage>
                          </FormControl>
                        </Flex>

                        <Flex
                          width={["100%", "100%", "100%", "80%", "35%"]}
                          flexDir={["row", "row", "row", "row"]}
                          alignItems="end"
                          h="4.5rem"
                          gap={2}
                        >
                          <Button
                            px={2}
                            width="100%"
                            size="md"
                            type="button"
                            onClick={() =>
                              push({
                                responseRouteType: values.responseRouteType,
                                property: "",
                                fieldToSave: "",
                                displayLabel: "",
                                entityToSave: "",
                                visibleInReport: false,
                              } as EngineConfigItem)
                            }
                          >
                            Adicionar Configuração
                          </Button>
                        </Flex>
                      </Flex>

                      <FormControl
                        isInvalid={
                          !!(
                            getFormError(errors, ["configs"]) &&
                            touched?.configs
                          )
                        }
                        mt={4}
                      >
                        <FormErrorMessage>
                          {getFormError(errors, ["configs"])}
                        </FormErrorMessage>
                      </FormControl>

                      <VStack width="100%" mt={6} align="start" spacing={4}>
                        {values?.configs?.map((config, configIndex) => (
                          <Box
                            key={configIndex}
                            w="100%"
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                            position="relative"
                          >
                            <Flex justify="space-between" align="center" mb={2}>
                              <Text fontWeight="500">
                                Configuração #{configIndex + 1}
                              </Text>
                              <CloseButton
                                onClick={() => {
                                  // se não há id ou está criando, é só local
                                  if (isCreate || !config.id) {
                                    remove(configIndex);
                                    return;
                                  }

                                  if (!isCreate && !config.id) {
                                    remove(configIndex);
                                    return;
                                  }

                                  handleDeleteConfigEngine.mutate(config.id, {
                                    onSuccess() {
                                      remove(configIndex);
                                    },
                                    onError(error: any) {
                                      toast({
                                        title: "Erro",
                                        description:
                                          error?.message ||
                                          "Erro ao excluir configuração do motor",
                                        status: "error",
                                        duration: 3000,
                                        isClosable: true,
                                      });
                                    },
                                  });
                                }}
                              />
                            </Flex>

                            {/* Campos da configuração */}
                            <Flex alignItems="start" gap={5} flexWrap="wrap">
                              <FormControl
                                isInvalid={
                                  !!getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "property",
                                  ]) && touched.configs?.[configIndex]?.property
                                }
                                flex="1"
                                minW="200px"
                              >
                                <FormLabel>Propriedade</FormLabel>
                                <Field
                                  as={Input}
                                  name={`configs[${configIndex}].property`}
                                  placeholder="Ex: inscricaoEmpregador"
                                />
                                <FormErrorMessage>
                                  {getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "property",
                                  ])}
                                </FormErrorMessage>
                              </FormControl>

                              <FormControl
                                isInvalid={
                                  !!getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "entityToSave",
                                  ]) &&
                                  touched.configs?.[configIndex]?.entityToSave
                                }
                                flex="1"
                                minW="200px"
                              >
                                <FormLabel>Entidade para Salvar</FormLabel>
                                <Field
                                  as={Select}
                                  name={`configs[${configIndex}].entityToSave`}
                                  placeholder="Selecione a entidade"
                                >
                                  {listEntityConfigEngine.map((entity: any) => (
                                    <option key={entity} value={entity}>
                                      {entity}
                                    </option>
                                  ))}
                                </Field>
                                <FormErrorMessage>
                                  {getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "entityToSave",
                                  ])}
                                </FormErrorMessage>
                              </FormControl>

                              <FormControl
                                isInvalid={
                                  !!getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "displayLabel",
                                  ]) &&
                                  touched.configs?.[configIndex]?.displayLabel
                                }
                                flex="1"
                                minW="200px"
                              >
                                <FormLabel>Label</FormLabel>
                                <Field
                                  as={Input}
                                  name={`configs[${configIndex}].displayLabel`}
                                  placeholder="Ex: Inscrição Empregador"
                                />
                                <FormErrorMessage>
                                  {getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "displayLabel",
                                  ])}
                                </FormErrorMessage>
                              </FormControl>

                              <FormControl flex="1" minW="200px">
                                <FormLabel>Campo para Salvar</FormLabel>
                                <Field
                                  as={Input}
                                  name={`configs[${configIndex}].fieldToSave`}
                                  placeholder="Ex: nomeCampoBackend"
                                />
                                <FormErrorMessage>
                                  {getFormError(errors, [
                                    "configs",
                                    configIndex,
                                    "fieldToSave",
                                  ])}
                                </FormErrorMessage>
                              </FormControl>

                              <FormControl flex="1" minW="200px">
                                <Flex align="center" gap={3} flexDir="column">
                                  <FormLabel mb={0}>
                                    Visível no Relatório
                                  </FormLabel>
                                  <Switch
                                    isChecked={config.visibleInReport}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `configs[${configIndex}].visibleInReport`,
                                        e.target.checked
                                      )
                                    }
                                    colorScheme="blue"
                                  />
                                </Flex>
                              </FormControl>
                            </Flex>
                          </Box>
                        ))}
                      </VStack>
                    </>
                  )}
                </FieldArray>
                <Flex justifyContent="end" mt={4}>
                  <Button
                    type="submit"
                    colorScheme="primary"
                    gap={2}
                    isLoading={
                      handleCreateConfigEngine.isPending ||
                      handleUpdateConfigEngine.isPending
                    }
                  >
                    {handleCreateConfigEngine.isPending ||
                    handleUpdateConfigEngine.isPending ? (
                      <>
                        <Spinner size="sm" />{" "}
                        {isCreate ? "Salvar" : "Atualizar"}
                      </>
                    ) : isCreate ? (
                      "Salvar"
                    ) : (
                      "Atualizar"
                    )}
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Box>
  );
}
